/**
 * Utilitat per codificar i decodificar els paràmetres de sessió de l'IDE SQL.
 * Impedeix que els alumnes puguin manipular l'adreça URL fàcilment (obscuriment / signat lleuger).
 */

export interface IdeSqlSessionParams {
    idPractica?: string;
    idProblema?: string;
    openPanel?: 'none' | 'notes' | 'enunciat';
    isExamen?: boolean;
    isDesafio?: boolean;
    fromGame?: boolean;
    userId?: string;
    timestamp?: number;
    engine?: 'mysql' | 'mongodb';
}

// Clau de barreja per a l'ofuscació de la URL
const SALT = "Lanterns_SQL_Ide_2026";

function xorEncode(str: string, key: string): string {
    let result = '';
    for (let i = 0; i < str.length; i++) {
        result += String.fromCharCode(str.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
}

/**
 * Genera el token xifrat/ofuscat a partir dels paràmetres
 */
export function buildIdeSqlToken(params: IdeSqlSessionParams): string {
    try {
        const payload = JSON.stringify({
            p: params.idPractica || '',
            pr: params.idProblema || '',
            pan: params.openPanel || 'none',
            ex: params.isExamen ? 1 : 0,
            des: params.isDesafio ? 1 : 0,
            gm: params.fromGame ? 1 : 0,
            u: params.userId || '',
            t: params.timestamp || Date.now(),
            eng: params.engine || 'mysql'
        });

        const xored = xorEncode(payload, SALT);
        // Base64 segur per a URL
        const base64 = btoa(unescape(encodeURIComponent(xored)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        return base64;
    } catch (e) {
        console.error("Error xifrant sessió IDE:", e);
        return '';
    }
}

/**
 * Descodifica el token de la URL recuperant els paràmetres originals
 */
export function parseIdeSqlToken(token: string): IdeSqlSessionParams | null {
    if (!token) return null;
    try {
        let base64 = token.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) {
            base64 += '=';
        }
        const decodedStr = decodeURIComponent(escape(atob(base64)));
        const rawJson = xorEncode(decodedStr, SALT);
        const obj = JSON.parse(rawJson);

        return {
            idPractica: obj.p || '',
            idProblema: obj.pr || '',
            openPanel: obj.pan || 'none',
            isExamen: Boolean(obj.ex),
            isDesafio: Boolean(obj.des),
            fromGame: Boolean(obj.gm),
            userId: obj.u || '',
            timestamp: obj.t || 0,
            engine: obj.eng === 'mongodb' ? 'mongodb' : 'mysql'
        };
    } catch (e) {
        console.warn("No s'ha pogut desxifrar el token SQL:", e);
        return null;
    }
}

/**
 * Construeix la URL completa per obrir l'IDE
 */
export function getIdeSqlUrl(params: IdeSqlSessionParams): string {
    const token = buildIdeSqlToken(params);
    return `/database/ide/frame?session=${encodeURIComponent(token)}`;
}

export function getIdeMongoUrl(params: IdeSqlSessionParams): string {
    const token = buildIdeSqlToken({ ...params, engine: 'mongodb' });
    return `/database/ide/frame?session=${encodeURIComponent(token)}`;
}
